import { BorrowerEntity } from "./borrowers";

export type CitizenshipStatus = "us_citizen" | "permanent_resident" | "non_pr_alien" | "none";
export type PropertyOwnershipInterestUsage = "PR" | "SH" | "IP";
export type PropertyOwnershipInterestTitleOwnership = "S" | "SP" | "O";
export type PartyToLawsuitPaymentType = "once" | "monthly";
export type NoteComakerStatus = "current" | "past_due" | "defaulted";

export interface Declaration {
  id: number;
  borrowerEntity: BorrowerEntity;
  citizenship?: Citizenship;
  propertyUsage?: PropertyUsages;
  propertyOwnershipInterest?: PropertyOwnershipInterest;
  outstandingJudgments: boolean;
  bankruptcy: Bankruptcy;
  foreclosedOrGivenTitle: boolean;
  partyToLawsuit?: PartyToLawsuit;
  loanResultingInForeclosure?: LoanResultingInForeclosure;
  delinquentOrDefaulted?: DelinquentOrDefaulted;
  alimony?: Alimony;
  borrowedDownpayment?: BorrowedDownpayment;
  noteComaker?: NoteComaker;
  createdAt: string;
  updatedAt: string;

  affiliatedWithSeller: boolean;
  applyingForMortgageOnOther: boolean;
  applyingForCredit: boolean;
  propertySubjectToALien: boolean;
  preForeclosureSaleInPastSevenYears: boolean;
}

export interface Citizenship {
  status: CitizenshipStatus;
  explanation: string;
  confirmation?: boolean;
}

export enum CitizenshipStatuses {
  usCitizen = "us_citizen",
  permanentResident = "permanent_resident",
  nonPermanentResident = "non_pr_alien",
  other = "none",
}

export interface PropertyOwnershipInterest {
  usage: PropertyOwnershipInterestUsage;
  titleOwnership: PropertyOwnershipInterestTitleOwnership;
}

export enum PropertyUsages {
  primary = "primary",
  secondary = "secondary",
  investment = "investment",
  neither = "neither",
}

export enum PropertyOwnershipUsages {
  principle = "PR",
  secondHome = "SH",
  investment = "IP",
}

export enum PropertyTitleOwnerships {
  solely = "S",
  jointlyWithPartner = "SP",
  jointlyWithOther = "O",
}

export interface PartyToLawsuit {
  rulingReceived: boolean;
  paymentType?: PartyToLawsuitPaymentType;
  paymentAmount?: number;
}

export enum PartyToLawsuitPaymentTypes {
  onceOff = "once",
  monthly = "monthly",
}

export enum BankruptcyType {
  ChapterSeven = "Chapter 7",
  ChapterEleven = "Chapter 11",
  ChapterTwelve = "Chapter 12",
  ChapterThirteen = "Chapter 13",
}

export enum BankruptcyTypeKeys {
  ChapterSeven = "chapter_7",
  ChapterEleven = "chapter_11",
  ChapterTwelve = "chapter_12",
  ChapterThirteen = "chapter_13",
}

export const BankruptcyOptions = [
  {
    label: BankruptcyType.ChapterSeven,
    value: "chapter_7",
  },
  {
    label: BankruptcyType.ChapterEleven,
    value: "chapter_11",
  },
  {
    label: BankruptcyType.ChapterTwelve,
    value: "chapter_12",
  },
  {
    label: BankruptcyType.ChapterThirteen,
    value: "chapter_13",
  },
];

export const BankruptcyTypes = [
  BankruptcyType.ChapterSeven,
  BankruptcyType.ChapterEleven,
  BankruptcyType.ChapterTwelve,
  BankruptcyType.ChapterThirteen,
];

export interface Bankruptcy {
  bankruptcyType: BankruptcyTypeKeys[];
}

export interface LoanResultingInForeclosure {
  explanation: string;
}

export interface DelinquentOrDefaulted {
  explanation: string;
}

export interface Alimony {
  monthlyPayment: number;
  firstPaymentDate: string;
  mostRecentPaymentDate: string;
}

export interface BorrowedDownpayment {
  explanation?: string;
  amount: number;
}

export interface NoteComaker {
  balanceDue: number;
  status: NoteComakerStatus;
  monthlyPayment: number;
  firstPaymentDate: string;
  mostRecentPaymentDate: string;
}

export enum NoteCokmakerStatuses {
  current = "current",
  pastDue = "past_due",
  defaulted = "defaulted",
}
